import { AI_JOBS_FUNCTION_PATH, checkAiJobsResponse, type AiJobsRequest, type AiJobsResponse } from '../../supabase/functions/_shared/aiJobsProtocol';
import { platformFailureCode, type AiTransportFailure } from './aiClient';

// Client transport to the AI JOBS function (supabase/functions/ai-jobs). Same discipline as
// src/backend/aiClient.ts: the request carries the signed-in user's JWT, no provider key exists in
// this bundle, and every outcome is normalized to a code the UI can translate. The one thing this
// transport must never do is decide that a request that got no answer FAILED: a submit whose
// answer was lost may well have been accepted (Owner contract 2026-09-13 §20), and the store
// settles that by asking for the job's status.
export type AiJobsCallOutcome = { kind: 'response'; response: AiJobsResponse; httpStatus: number } | { kind: 'failed'; code: AiTransportFailure; message: string };

export interface AiJobsTransportOptions {
  accessToken: string | null;
  timeoutMs?: number;
}

export type AiJobsTransport = (request: AiJobsRequest, options: AiJobsTransportOptions) => Promise<AiJobsCallOutcome>;

// A submit uploads photos and is answered as soon as the job is durable (not after the analysis).
export const AI_JOBS_SUBMIT_TIMEOUT_MS = 90_000;
export const AI_JOBS_STATUS_TIMEOUT_MS = 20_000;
// Removing this phone's push registration happens INSIDE a sign-out, which waits for it: a few
// seconds at most, then the inspector is signed out whether the server answered or not.
export const AI_JOBS_UNREGISTER_TIMEOUT_MS = 4_000;

export function aiJobsEndpoint(): string | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  return base.length > 0 ? `${base}${AI_JOBS_FUNCTION_PATH}` : null;
}

let configuredForTests: boolean | null = null;

export function aiJobsBackendConfigured(): boolean {
  if (configuredForTests !== null) return configuredForTests;
  return aiJobsEndpoint() !== null && ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim().length > 0;
}

export function setAiJobsBackendConfiguredForTests(value: boolean | null): void {
  configuredForTests = value;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export const fetchAiJobsTransport: AiJobsTransport = async (request, options) => {
  const endpoint = aiJobsEndpoint();
  const anon = ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
  if (!endpoint || anon.length === 0) return { kind: 'failed', code: 'notConfigured', message: 'AI jobs backend is not configured' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { kind: 'failed', code: 'offline', message: 'device is offline' };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? AI_JOBS_STATUS_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: anon, ...(options.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}) },
      body: JSON.stringify(request),
      signal: controller.signal
    });
  } catch (raw) {
    clearTimeout(timer);
    const aborted = raw instanceof Error && raw.name === 'AbortError';
    return { kind: 'failed', code: aborted ? 'timeout' : 'network', message: aborted ? 'request timed out' : 'network error' };
  }
  clearTimeout(timer);
  let text = '';
  try {
    text = await res.text();
  } catch {
    text = '';
  }
  const parsed = checkAiJobsResponse(parseJson(text));
  if (!parsed) return { kind: 'failed', code: res.status === 404 ? 'notConfigured' : platformFailureCode(res.status), message: `unexpected answer (HTTP ${res.status})` };
  return { kind: 'response', response: parsed, httpStatus: res.status };
};

let override: AiJobsTransport | null = null;

export function setAiJobsTransportForTests(transport: AiJobsTransport | null): void {
  override = transport;
}

export function getAiJobsTransport(): AiJobsTransport {
  return override ?? fetchAiJobsTransport;
}
